import { Search, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '../primitives/button';
import { Input } from '../primitives/input';
import { cn } from '../lib/cn';

interface FilterBarProps {
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  /** Labelled selects in the same row as the search; their labels are read out but not shown */
  children?: ReactNode;
  /** Status chips under the search */
  chips?: ReactNode;
  /** "Showing 12 of 40 jobs" */
  summary?: ReactNode;
  hasActiveFilters?: boolean;
  onClear?: () => void;
  clearLabel?: string;
  className?: string;
}

/**
 * The same compact filters on every list page: one row with the search and selects, then the chips with the count
 * and Clear on the right. No card, so it takes as little room as possible.
 */
export function FilterBar({
  search,
  onSearchChange,
  searchPlaceholder = 'Search',
  children,
  chips,
  summary,
  hasActiveFilters = false,
  onClear,
  clearLabel = 'Clear filters',
  className,
}: FilterBarProps) {
  const clear = hasActiveFilters && onClear && (
    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={onClear}>
      <X />
      {clearLabel}
    </Button>
  );
  return (
    // Sizes by its own width (a container query), so it fits a narrow list column as well as a full page
    <div className={cn('@container grid min-w-0 gap-2.5', className)}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full min-w-0 @xl:w-auto @xl:min-w-52 @xl:flex-1">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            className="h-10 rounded-xl border-transparent bg-surface pl-10 shadow-sm"
          />
        </div>
        {children && (
          <div
            className={cn(
              'grid w-full grid-cols-2 gap-2 @xl:flex @xl:w-auto @xl:flex-wrap @xl:[&>*]:w-44',
              // Select labels stay for screen readers; the trigger shows the chosen value
              '[&_label]:sr-only [&_[role=combobox]]:h-10 [&_[role=combobox]]:rounded-xl',
              '[&_[role=combobox]]:border-transparent [&_[role=combobox]]:bg-surface [&_[role=combobox]]:shadow-sm',
            )}
          >
            {children}
          </div>
        )}
      </div>
      {(chips || summary || clear) && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">{chips}</div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            {summary}
            {clear}
          </div>
        </div>
      )}
    </div>
  );
}

interface FilterChipsProps<T extends string> {
  label: string;
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: string; count?: number }[];
}

/** Single-choice chips, each with the number of rows it would show. */
export function FilterChips<T extends string>({ label, value, onChange, options }: FilterChipsProps<T>) {
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-1.5">
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(option.value)}
            className={cn(
              'inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-xs font-semibold transition-colors',
              'focus-visible:outline-2 focus-visible:outline-ring',
              active
                ? 'bg-secondary text-secondary-foreground'
                : 'bg-surface text-muted-foreground shadow-sm hover:text-secondary',
            )}
          >
            {option.label}
            {option.count !== undefined && (
              <span
                className={cn(
                  'rounded-full px-1.5 text-[0.6875rem] tabular-nums',
                  active ? 'bg-secondary-foreground/20' : 'bg-muted',
                )}
              >
                {option.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

interface SearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** Also the accessible name */
  placeholder: string;
  className?: string;
}

/** A search box on its own, e.g. beside a page title */
export function SearchField({ value, onChange, placeholder, className }: SearchFieldProps) {
  return (
    <div className={cn('relative min-w-0', className)}>
      <Search className="pointer-events-none absolute left-4 top-1/2 size-5 -translate-y-1/2 text-muted-foreground" />
      <Input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="h-12 rounded-xl border-border bg-surface pl-12 text-sm"
      />
    </div>
  );
}

/** Single-choice tabs with an orange underline on the chosen one, each with its count; same API as FilterChips */
export function UnderlineTabs<T extends string>({ label, value, onChange, options }: FilterChipsProps<T>) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="flex gap-6 overflow-x-auto border-b border-transparent sm:gap-8 [scrollbar-width:none]"
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(option.value)}
            className={cn(
              'relative inline-flex h-11 shrink-0 items-center gap-2 text-sm font-medium transition-colors',
              'focus-visible:outline-2 focus-visible:outline-ring',
              'after:absolute after:inset-x-0 after:bottom-0 after:h-[3px] after:rounded-full after:transition-colors',
              active
                ? 'text-secondary after:bg-primary'
                : 'text-secondary/80 after:bg-transparent hover:text-secondary',
            )}
          >
            {option.label}
            {option.count !== undefined && option.count > 0 && (
              <span
                className={cn(
                  'rounded-md px-1.5 text-xs font-semibold tabular-nums',
                  active ? 'text-secondary' : 'bg-primary-soft text-secondary',
                )}
              >
                {option.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
